import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Receipt, Loader2, RefreshCw, Tag, Wallet } from 'lucide-react';

const fmtMoney = (n, cur = 'USD') => {
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(n); }
  catch { return `$${Number(n).toFixed(2)}`; }
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

const initials = (name, email) => {
  const src = (name || email || '?').trim();
  return src[0]?.toUpperCase() || '?';
};

export default function PaymentsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try { const r = await api.get('/admin/payments'); setData(r.data); }
    catch (e) { if (e?.response?.status !== 403) console.debug('payments load failed', e); }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-card p-5 flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]" data-testid="payments-dashboard-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading payments…
      </div>
    );
  }
  if (!data) return null;

  const items = data.items || [];

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-card p-5" data-testid="payments-dashboard">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl grid place-items-center shrink-0" style={{ background: 'hsl(var(--accent))' }}>
          <Receipt className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-600">Payments</h3>
            <Badge variant="secondary" className="text-[10px]">Owner</Badge>
          </div>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Your most recent successful payments.</p>
        </div>
        <Button variant="ghost" size="sm" className="rounded-lg h-8 gap-1.5" onClick={() => load(true)} disabled={refreshing} data-testid="payments-refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="rounded-lg bg-[hsl(var(--surface-2))] p-3 flex items-center gap-3" data-testid="payments-stat-gross">
          <Wallet className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
          <div><p className="font-display text-xl font-700">{fmtMoney(data.gross, data.currency)}</p><p className="text-[10px] text-[hsl(var(--muted-foreground))]">gross (recent)</p></div>
        </div>
        <div className="rounded-lg bg-[hsl(var(--surface-2))] p-3 flex items-center gap-3" data-testid="payments-stat-count">
          <Receipt className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
          <div><p className="font-display text-xl font-700">{data.count}</p><p className="text-[10px] text-[hsl(var(--muted-foreground))]">payments</p></div>
        </div>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-[hsl(var(--muted-foreground))] py-8 text-center" data-testid="payments-empty">
          No payments yet — successful purchases will show up here.
        </p>
      ) : (
        <div className="space-y-2">
          {items.map((p) => (
            <div key={p.session_id} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] p-3" data-testid={`payment-row-${p.session_id}`}>
              <div className="h-9 w-9 rounded-full grid place-items-center text-white text-sm font-600 shrink-0" style={{ background: 'hsl(var(--primary))' }}>
                {initials(p.name, p.email)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-600 truncate">{p.name || p.email || 'Customer'}</span>
                  <Badge variant="secondary" className="text-[10px]">{p.package_label}</Badge>
                  {p.code ? (
                    <Badge variant="secondary" className="text-[10px] gap-1"><Tag className="h-3 w-3" />{p.code}</Badge>
                  ) : null}
                </div>
                <p className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{p.email} · {fmtDate(p.date)}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-display font-700" style={{ color: 'hsl(var(--success))' }} data-testid={`payment-amount-${p.session_id}`}>{fmtMoney(p.amount, p.currency)}</p>
                {p.discount > 0 ? <p className="text-[10px] text-[hsl(var(--muted-foreground))]">-{fmtMoney(p.discount, p.currency)} off</p> : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
